/**
 * Idle session timeout — déconnexion après inactivité (PRD 4.36.0)
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useAppToast } from '../contexts/AppToastContext.jsx';
import { useConnectivity } from '../contexts/ConnectivityContext.jsx';
import { useOfflineSync } from '../contexts/OfflineSyncContext.jsx';
import * as authService from '../services/auth.service.js';

const WARN_BEFORE_MS = 2 * 60 * 1000;
const THROTTLE_MS = 1000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll'];

const SESSION_IDLE_STORAGE_KEY = 'sessionIdleTimeoutMinutes';

/**
 * @param {number | null | undefined} minutes
 * @returns {number | null}
 */
function normalizeIdleMinutes(minutes) {
  const n = parseInt(String(minutes), 10);
  if (Number.isNaN(n) || n <= 0) return null;
  return n;
}

/**
 * @returns {Promise<number | null>}
 */
async function resolveIdleTimeoutMinutes() {
  const stored = sessionStorage.getItem(SESSION_IDLE_STORAGE_KEY);
  const fromStorage = normalizeIdleMinutes(stored);
  if (fromStorage !== null) return fromStorage;

  try {
    const response = await authService.getSessionPolicy();
    const mins = normalizeIdleMinutes(response?.data?.idleTimeoutMinutes);
    if (mins !== null) {
      sessionStorage.setItem(SESSION_IDLE_STORAGE_KEY, String(mins));
    }
    return mins;
  } catch {
    return normalizeIdleMinutes(30);
  }
}

export function useIdleSessionTimeout() {
  const { isAuthenticated, logout } = useAuth();
  const { showInfo } = useAppToast();
  const { isReachable } = useConnectivity();
  const { pendingCount } = useOfflineSync();
  const idleMinutesRef = useRef(null);
  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const lastActivityRef = useRef(0);

  const clearTimers = useCallback(() => {
    if (warnTimerRef.current) {
      clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  const handleIdleExpiry = useCallback(() => {
    showInfo(
      'Session fermée pour inactivité.',
      'Déconnexion automatique',
    );
    window.setTimeout(() => {
      logout({ reason: 'idle' });
    }, 1200);
  }, [logout, showInfo]);

  const scheduleTimers = useCallback(() => {
    clearTimers();
    const minutes = idleMinutesRef.current;
    if (!minutes || minutes <= 0) return;
    if (!isReachable || pendingCount > 0) return;

    const totalMs = minutes * 60 * 1000;
    const warnDelayMs = Math.max(totalMs - WARN_BEFORE_MS, Math.floor(totalMs * 0.5));

    warnTimerRef.current = window.setTimeout(() => {
      showInfo(
        'Bougez la souris ou appuyez sur une touche pour rester connecté.',
        'Votre session va expirer',
      );
    }, warnDelayMs);

    logoutTimerRef.current = window.setTimeout(() => {
      handleIdleExpiry();
    }, totalMs);
  }, [clearTimers, handleIdleExpiry, showInfo, isReachable, pendingCount]);

  const resetIdleTimer = useCallback(() => {
    if (!idleMinutesRef.current) return;
    scheduleTimers();
  }, [scheduleTimers]);

  useEffect(() => {
    if (!isReachable || pendingCount > 0) {
      clearTimers();
    } else if (isAuthenticated && idleMinutesRef.current) {
      resetIdleTimer();
    }
  }, [isReachable, pendingCount, isAuthenticated, clearTimers, resetIdleTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      idleMinutesRef.current = null;
      return undefined;
    }

    let cancelled = false;

    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current < THROTTLE_MS) return;
      lastActivityRef.current = now;
      resetIdleTimer();
    };

    (async () => {
      const minutes = await resolveIdleTimeoutMinutes();
      if (cancelled) return;
      idleMinutesRef.current = minutes;
      if (!minutes) return;

      lastActivityRef.current = Date.now();
      if (isReachable && pendingCount === 0) {
        scheduleTimers();
      }
      for (const eventName of ACTIVITY_EVENTS) {
        window.addEventListener(eventName, onActivity, { passive: true });
      }
    })();

    return () => {
      cancelled = true;
      clearTimers();
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, onActivity);
      }
    };
  }, [isAuthenticated, clearTimers, resetIdleTimer, scheduleTimers, isReachable, pendingCount]);
}
